import { useEffect } from "react";

/**
 * Global hook to ensure only one iframe-based player (like YouTube, Omny podcast, etc.)
 * plays at a time. Works by listening to postMessage events and pausing
 * all other iframes of the same type when one starts playing.
 */
export function useGlobalMediaControl() {
  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
      let data: any = event.data;
      if (!data) return;

      // Case 1: Dugout messages start with "dugout://"
      if (typeof data === "string" && data.startsWith("dugout://")) {
        try {
          const jsonPart = data.replace("dugout://", "");
          const parsed = JSON.parse(jsonPart) as object;
          data = { platform: "dugout", ...parsed };
        } catch (err) {
          console.warn(
            "[GlobalMediaControl] Failed to parse Dugout event",
            err,
          );
          return;
        }
      }

      // Case 2: Generic string messages (JSON or not)
      else if (typeof data === "string") {
        try {
          data = JSON.parse(data) as object;
        } catch {
          // ignore non-JSON strings like "ready" etc.
          return;
        }
      }

      // --- YouTube ---
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (data?.event === "onStateChange" && data?.info === 1) {
        pauseOtherIframes(event.source, true);
      }

      // --- Omny.fm ---
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (data?.context === "player.js" && data?.event === "play") {
        pauseOtherIframes(event.source, true);
      }

      // --- Dugout ---
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (data?.platform === "dugout" && data?.id === "playStart") {
        pauseOtherIframes(event.source, true);
      }

      // --- TikTok ---
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (data?.event === "play" && data?.source === "tiktok-embed") {
        pauseOtherIframes(event.source, true);
      }
    }

    window.addEventListener("message", handleMessage);

    // Global pause handler for other players to react to
    const handlePauseAllPlayers = (
      e: CustomEvent<{ source: MessageEventSource }>,
    ) => {
      pauseOtherIframes(e.detail.source, false);
    };
    window.addEventListener(
      "pauseAllPlayers",
      handlePauseAllPlayers as EventListener,
    );

    return () => {
      window.removeEventListener("message", handleMessage);
      window.removeEventListener(
        "pauseAllPlayers",
        handlePauseAllPlayers as EventListener,
      );
    };
  }, []);
}

/**
 * Helper: Pause all other matching iframe players
 */
function pauseOtherIframes(
  source: MessageEventSource | null,
  triggerEvent?: boolean,
) {
  const iframes = document.querySelectorAll("iframe");

  iframes.forEach((el) => {
    const iframe = el;
    if (!iframe.contentWindow || iframe.contentWindow === source) return;

    const src = iframe.getAttribute("src") || "";

    try {
      if (src.includes("youtube")) {
        iframe.contentWindow.postMessage(
          JSON.stringify({ event: "command", func: "pauseVideo" }),
          "*",
        );
      } else if (src.includes("omny.fm")) {
        iframe.contentWindow.postMessage(
          JSON.stringify({ context: "player.js", method: "pause" }),
          "*",
        );
      } else if (src.includes("dugout")) {
        // Dugout custom pause message
        iframe.contentWindow.postMessage('dugout://{"id":"playPause"}', "*");
      } else if (src.includes("tiktok")) {
        iframe.contentWindow.postMessage(
          JSON.stringify({ type: "pause", value: null }),
          "*",
        );
      }
    } catch (err) {
      console.warn(`[GlobalMediaControl] Failed to pause ${src}:`, err);
    }
  });

  // Notify other hooks/components
  if (triggerEvent) {
    window.dispatchEvent(
      new CustomEvent("pauseAllPlayers", { detail: { source: source } }),
    );
  }
}
